/**
 * Leituras do modulo de leads. Rotas que o backend vai ter:
 *
 *   GET /api/leads?from&to&limit&offset&sort&dir&q&situacao&oferta&canal&funil&alto
 *   GET /api/leads/summary?from&to
 *   GET /api/leads/:id
 *
 * `companyId` entra na assinatura por dois motivos e NAO viaja na query: (1) o
 * gerador simulado usa a empresa em foco para volume, produtos, comissao e
 * equipe; (2) o `useCallback` da pagina refaz a busca quando a administracao
 * troca a empresa. O tenant real continua vindo da sessao (lib/api.ts,
 * decisao 1).
 *
 * `/api/leads` JA EXISTE no backend, mas devolve so o `LeadListItem`
 * compartilhado e ignora filtros. `normalizarPagina` completa os campos que
 * faltam (situacao aproximada, facetas contadas sobre a pagina e marcadas
 * como parciais) para a tela funcionar hoje e trocar para o servidor sem
 * mexer em componente.
 *
 * `/api/leads/:id` EXISTE desde 25/09/2026 (src/api/leadDetail.ts), com a
 * linha do tempo real. O 404 `lead_nao_encontrado` vira `null` ("lead nao
 * encontrado" agora e verdade sobre o banco: inexistente OU de outra
 * empresa, a mesma resposta). A venda assistida cujo autor foi apagado chega
 * com `sellerName: null` e ganha o rotulo "usuário removido" aqui, para os
 * componentes continuarem recebendo texto. `/api/leads/summary` ainda nao
 * existe: cai em `ErrorPanel` (404), nunca em numero inventado.
 */

import { ApiError, fetchOrSimulate, rangeQuery, type Envelope } from '../../lib/api';
import type { LeadListItem, Page, PeriodRange } from '../../types';
import { contarFacetas, situacaoAproximada } from './filtros';
import { limiarAltoValor, simularDetalheDoLead, simularListaDeLeads, simularResumoDeLeads } from './mock';
import type { ConsultaDeLeads, LeadDetail, LeadListRow, PaginaDeLeads, ResumoDeLeads, TimelineEvent } from './types';

/** Como o servidor manda: igual ao `LeadDetail`, mas a venda assistida pode vir sem autor. */
type TimelineRecebida = TimelineEvent | (Omit<Extract<TimelineEvent, { kind: 'seller_sale' }>, 'sellerName'> & { readonly sellerName: string | null });
type DetalheRecebido = Omit<LeadDetail, 'timeline'> & { readonly timeline: readonly TimelineRecebida[] };

const AUTOR_REMOVIDO = 'usuário removido';

function normalizarDetalhe(recebido: DetalheRecebido): LeadDetail {
  return {
    ...recebido,
    timeline: recebido.timeline.map((e): TimelineEvent => (e.kind === 'seller_sale' ? { ...e, sellerName: e.sellerName ?? AUTOR_REMOVIDO } : e)),
  };
}

export async function fetchLeadDetail(companyId: string, leadId: string, signal: AbortSignal): Promise<Envelope<LeadDetail | null>> {
  try {
    const envelope = await fetchOrSimulate<DetalheRecebido | null>(`/api/leads/${encodeURIComponent(leadId)}`, {}, signal, () =>
      simularDetalheDoLead(companyId, leadId),
    );
    return { data: envelope.data === null ? null : normalizarDetalhe(envelope.data), simulated: envelope.simulated };
  } catch (error) {
    if (error instanceof ApiError && error.status === 404 && error.code === 'lead_nao_encontrado') {
      return { data: null, simulated: false };
    }
    throw error;
  }
}

export function fetchResumoDeLeads(companyId: string, range: PeriodRange, signal: AbortSignal): Promise<Envelope<ResumoDeLeads>> {
  return fetchOrSimulate('/api/leads/summary', rangeQuery(range), signal, () => simularResumoDeLeads(companyId, range));
}

/** O que o backend de hoje manda (base) ou o que vai mandar (linha completa). */
type ItemRecebido = LeadListItem & Partial<Omit<LeadListRow, keyof LeadListItem>>;
type PaginaRecebida = Page<ItemRecebido> & Partial<Pick<PaginaDeLeads, 'facets' | 'highValueThresholdCents'>>;

/** Sem limiar do servidor, vale o percentil 80 dos itens carregados (aproximacao, marcada como parcial pelas facetas). */
function limiarSobreOsItens(items: readonly LeadListItem[]): number {
  return limiarAltoValor(items.map((i) => i.amountCents));
}

function completarLinha(item: ItemRecebido, limiar: number): LeadListRow {
  return {
    ...item,
    funnel: item.funnel ?? 'carrinho_abandonado',
    offerId: item.offerId ?? null,
    situacao: item.situacao ?? situacaoAproximada(item),
    contacts: item.contacts ?? item.voiceAttempts,
    lastContact: item.lastContact ?? null,
    nextTouch: item.nextTouch ?? null,
    highValue: item.highValue ?? item.amountCents >= limiar,
  };
}

/** Completa o que o servidor ainda nao manda. Quando ele mandar tudo, devolve como veio. */
export function normalizarPagina(recebida: PaginaRecebida): PaginaDeLeads {
  const limiar = recebida.highValueThresholdCents ?? limiarSobreOsItens(recebida.items);
  const items = recebida.items.map((i) => completarLinha(i, limiar));
  return {
    items,
    total: recebida.total,
    limit: recebida.limit,
    offset: recebida.offset,
    facets: recebida.facets ?? contarFacetas(items, true),
    highValueThresholdCents: limiar,
  };
}

export async function fetchListaDeLeads(companyId: string, range: PeriodRange, consulta: ConsultaDeLeads, signal: AbortSignal): Promise<Envelope<PaginaDeLeads>> {
  const query = {
    ...rangeQuery(range),
    limit: String(consulta.limit),
    offset: String(consulta.offset),
    sort: consulta.sort,
    dir: consulta.dir,
    q: consulta.q.trim() === '' ? undefined : consulta.q.trim(),
    situacao: consulta.situacao ?? undefined,
    oferta: consulta.offerId ?? undefined,
    canal: consulta.canal ?? undefined,
    funil: consulta.funnel ?? undefined,
    alto: consulta.altoValor ? '1' : undefined,
  };
  const envelope = await fetchOrSimulate<PaginaRecebida>('/api/leads', query, signal, () => simularListaDeLeads(companyId, range, consulta));
  return { data: normalizarPagina(envelope.data), simulated: envelope.simulated };
}
